// src/components/OrdineCaviTab.js
// Tab facoltativo di commessa (toggle in "Personalizza commessa") — stesso identico modello
// dello strumento standalone "Ordine Cavi Fornitore" (Strumenti Tecnici): elenco cavi a 13
// colonne (Quadro/Pos./Sigla/Descrizione/Tensione/Interr./Lunghezza/Tipo/Colore/Formazione/
// Origine/Destinaz./Note), raggruppato in automatico per Tipo/Colore/Formazione, con sezione
// dati mancanti e importazione da Excel nello stesso formato. Qui però persistito su Firestore
// per poterci tornare sopra dopo giorni, con un vero flusso di invio a Ufficio Acquisti: bozza
// modificabile da chiunque lavori sulla commessa, poi bloccata all'invio — riapribile solo da
// chi ha il permesso Centro Acquisti (o titolare/amministrazione), che può anche solo
// aggiungere una nota senza dover riaprire tutto. Stesso schema bozza/inviata/riapri già usato
// per le Note Spese.
import React, { useState, useEffect, useRef } from "react";
import { doc, onSnapshot, setDoc, serverTimestamp, getDocs, query, collection, where } from "firebase/firestore";
import { db } from "../firebase";
import { toLocalDateStr } from "../utils/noleggiUtils";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { logModifica } from "../utils/logModifica";
import * as XLSX from "xlsx";
import { Plus, Trash2, Printer, Send, RotateCcw, Lock, AlertTriangle, Upload, Info, Copy } from "lucide-react";
import { PORTATA_B1_PVC_RAME, RESISTIVITA_RAME, estraiSezioneDaFormazione, estraiCorrenteDaInterruttore, tipoDaTensione } from "../utils/portataCaviUtils";

const COLONNE = [
  { key: "quadro", label: "Quadro", w: 90 },
  { key: "pos", label: "Pos.", w: 60 },
  { key: "sigla", label: "Sigla Cavo", w: 110 },
  { key: "descrizione", label: "Descrizione Collegamento", w: 200 },
  { key: "tensione", label: "Tensione", w: 90 },
  { key: "interr", label: "Interruttore", w: 100 },
  { key: "lunghezza", label: "L. (mt.)", w: 90 },
  { key: "tipo", label: "Tipo Cavo", w: 130 },
  { key: "colore", label: "Colore", w: 100 },
  { key: "formazione", label: "Formazione", w: 100 },
  { key: "origine", label: "Origine", w: 110 },
  { key: "destinazione", label: "Destinaz.", w: 110 },
  { key: "note", label: "Note", w: 150 },
];

const INTESTAZIONI_ATTESE = ["Quadro","Pos.","Sigla Cavo","Descrizione Collegamento","Tensione","Interr.","L. (mt.)","Tipo Cavo","Colore","Formazione","Origine","Destinaz.","Note"];

const inp = { width:"100%", padding:"5px 6px", border:"1px solid #e5e7eb", borderRadius:5, fontSize:11.5, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" };

// Il testo lo scrive un collega (sigla, descrizione, note, riferimento, nota acquisti...) e la
// stampa la apre un altro — senza escape, del codice scritto per errore o apposta in uno di
// questi campi finirebbe eseguito nel browser di chi stampa. Mai interpolare direttamente.
function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
}

let contatoreId = 1;
function nuovoId() { return contatoreId++; }
function rigaVuota() {
  return { id: nuovoId(), quadro:"", pos:"", sigla:"", descrizione:"", tensione:"", interr:"", lunghezza:"", tipo:"", colore:"", formazione:"", origine:"", destinazione:"", note:"" };
}

// Compatibilità con documenti salvati nel vecchio formato "formazioni scomposte" (prima di
// questa riscrittura) — converte in un elenco cavi minimale, così una bozza già iniziata non
// va persa, solo semplificata: un cavo per ogni combinazione formazione×collegamento.
function migraVecchioFormato(formazioniVecchie, righeVecchie) {
  const risultato = [];
  (righeVecchie||[]).forEach(r => {
    const nomeFormazione = r.formazione || (formazioniVecchie||[]).find(f=>f.id===Number(r.formazioneId))?.nome || "";
    const componenti = (formazioniVecchie||[]).filter(f => (f.nome||"").trim() === nomeFormazione.trim() || f.id===Number(r.formazioneId));
    const nColl = parseInt(r.collegamenti,10) || 1;
    for (let i=0;i<nColl;i++) {
      risultato.push({ ...rigaVuota(), formazione: nomeFormazione, lunghezza: r.lunghezza||"",
        tipo: componenti[0]?.componenti?.[0]?.etichetta ? "" : "" });
    }
  });
  return risultato.length ? risultato : [rigaVuota(), rigaVuota()];
}

export default function OrdineCaviTab({ projectId, orgId, projectNome, codiceCommessa, statoCommessa, isAdmin, currentUser }) {
  const { addToast } = useToast();
  const canSee = useCanSee();
  const canGestireAcquisti = isAdmin || canSee("centro-acquisti");
  const commessaChiusa = statoCommessa === "chiusa";

  const [caricato, setCaricato] = useState(false);
  const [stato, setStato] = useState("bozza");
  const [riferimento, setRiferimento] = useState("");
  const [righe, setRighe] = useState([rigaVuota(), rigaVuota(), rigaVuota()]);
  const [nota, setNota] = useState("");
  const [meta, setMeta] = useState({});
  const [salvataggioInCorso, setSalvataggioInCorso] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);
  const [erroreCaricamento, setErroreCaricamento] = useState(null);
  const [erroreImport, setErroreImport] = useState(null);
  const [suggerimentiStorici, setSuggerimentiStorici] = useState({ tipi: [], colori: [], formazioni: [] });
  const scriviIoStesso = useRef(false);
  const timerSalvataggio = useRef(null);
  const ultimoDatoNoto = useRef(null); // ultimo riferimento+righe conosciuto dal server — se lo stato attuale coincide, non c'è nulla da salvare davvero
  const fileInputRef = useRef(null);

  const inviato = stato === "inviato";
  const bloccato = inviato || commessaChiusa;

  useEffect(() => {
    if (!projectId) return;
    const rif = doc(db, "ordiniCavi", projectId);
    const unsub = onSnapshot(rif, snap => {
      if (scriviIoStesso.current) { scriviIoStesso.current = false; return; }
      let riferimentoCaricato = "", righeCaricate = [rigaVuota(), rigaVuota(), rigaVuota()];
      if (snap.exists()) {
        const d = snap.data();
        setStato(d.stato || "bozza");
        riferimentoCaricato = d.riferimento || "";
        setRiferimento(riferimentoCaricato);
        if (d.righe?.length && d.righe[0].formazioneId !== undefined) {
          // Vecchio formato "formazioni scomposte" — migrazione minimale, una tantum.
          righeCaricate = migraVecchioFormato(d.formazioni, d.righe);
        } else if (d.righe?.length) {
          righeCaricate = d.righe.map(r => ({ id: r.id ?? nuovoId(), ...r }));
        }
        setRighe(righeCaricate);
        setNota(d.nota || "");
        setMeta({ inviatoDaNome:d.inviatoDaNome, inviatoData:d.inviatoData, riapertoDaNome:d.riapertoDaNome, riapertoData:d.riapertoData });
        // Auto-correzione: documenti salvati con nome o codice commessa mancante o
        // disallineato (es. creati prima di una correzione, o durante un caricamento
        // anticipato) si risistemano da soli al primo caricamento, senza bisogno di ricrearli.
        if ((projectNome && d.projectNome !== projectNome) || (codiceCommessa && d.codiceCommessa !== codiceCommessa)) {
          scriviIoStesso.current = true;
          setDoc(doc(db, "ordiniCavi", projectId), { orgId, projectId, projectNome: projectNome||"", codiceCommessa: codiceCommessa||"" }, { merge: true }).catch(()=>{});
        }
      }
      // Fotografia di quello che il server conosce in questo momento: finché lo stato in
      // pagina resta identico a questo, non c'è nulla di nuovo da salvare — è così che si
      // evita di far lampeggiare "Salvataggio..." al solo aprire la pagina o quando arriva
      // un aggiornamento da un collega, senza che tu abbia scritto nulla.
      ultimoDatoNoto.current = JSON.stringify({ riferimento: riferimentoCaricato, righe: righeCaricate });
      setErroreCaricamento(null);
      setCaricato(true);
    }, err => {
      console.error("Errore caricamento ordine cavi:", err);
      setErroreCaricamento(err?.code === "permission-denied"
        ? "Permesso negato dal server. Se è la prima volta che usi questa funzione, potrebbe non essere ancora attiva per la tua organizzazione — contatta chi gestisce Nexiora."
        : "Errore di connessione. Riprova tra poco.");
      setCaricato(true);
    });
    return () => unsub();
  }, [projectId]);

  const salva = () => {
    if (!projectId || !caricato) return;
    if (timerSalvataggio.current) clearTimeout(timerSalvataggio.current);
    timerSalvataggio.current = setTimeout(async () => {
      setSalvataggioInCorso(true);
      scriviIoStesso.current = true;
      try {
        await setDoc(doc(db, "ordiniCavi", projectId), {
          orgId, projectId, projectNome: projectNome || "", codiceCommessa: codiceCommessa || "",
          riferimento, righe, nota, stato,
          updatedAt: serverTimestamp(),
        }, { merge: true });
        ultimoDatoNoto.current = JSON.stringify({ riferimento, righe });
      } catch (err) {
        console.error("Errore salvataggio ordine cavi:", err);
        addToast("Errore nel salvare: " + (err?.message || "errore sconosciuto"), "error");
      } finally {
        setSalvataggioInCorso(false);
      }
    }, 700);
  };

  useEffect(() => {
    if (!caricato || bloccato) return;
    const attuale = JSON.stringify({ riferimento, righe });
    if (attuale === ultimoDatoNoto.current) return; // niente di nuovo, non è farina di un utente
    salva();
  }, [riferimento, righe]);

  // Suggerimenti — presi da tutti gli Ordine Cavi già scritti nell'azienda (qualunque
  // commessa), una volta sola al caricamento: non serve tempo reale per un semplice
  // suggerimento di completamento. Niente indovinato, solo ciò che avete già scritto voi.
  useEffect(() => {
    if (!orgId) return;
    getDocs(query(collection(db, "ordiniCavi"), where("orgId", "==", orgId))).then(snap => {
      const tipi = new Set(), colori = new Set(), formazioni = new Set();
      snap.docs.forEach(d => {
        (d.data().righe || []).forEach(r => {
          if (r.tipo?.trim()) tipi.add(r.tipo.trim());
          if (r.colore?.trim()) colori.add(r.colore.trim());
          if (r.formazione?.trim()) formazioni.add(r.formazione.trim());
        });
      });
      setSuggerimentiStorici({ tipi: [...tipi], colori: [...colori], formazioni: [...formazioni] });
    }).catch(err => console.error("Errore caricamento suggerimenti storici:", err));
  }, [orgId]);

  const salvaNota = (valore) => {
    setNota(valore);
    if (timerSalvataggio.current) clearTimeout(timerSalvataggio.current);
    timerSalvataggio.current = setTimeout(async () => {
      scriviIoStesso.current = true;
      await setDoc(doc(db, "ordiniCavi", projectId), { orgId, projectId, nota: valore, updatedAt: serverTimestamp() }, { merge: true });
    }, 700);
  };

  const invia = async () => {
    if (commessaChiusa) { addToast("Commessa chiusa — non è più possibile inviare l'ordine cavi", "error"); return; }
    if (azioneInCorso) return;
    setAzioneInCorso(true);
    try {
      scriviIoStesso.current = true;
      const oggi = toLocalDateStr(new Date());
      await setDoc(doc(db, "ordiniCavi", projectId), {
        orgId, projectId, projectNome: projectNome || "", codiceCommessa: codiceCommessa || "", riferimento, righe, nota,
        stato: "inviato", inviatoDa: currentUser?.uid || "", inviatoDaNome: currentUser?.displayName || "", inviatoData: oggi,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      logModifica({ orgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "ordiniCavi", refId: projectId, refNome: `Ordine Cavi — ${projectNome || ""}`,
        campo: "stato", campoLabel: "Ordine cavi inviato a Ufficio Acquisti", before: "bozza", after: "inviato",
        projectId, projectName: projectNome || "" });
      setStato("inviato");
      addToast("Inviato a Ufficio Acquisti ✅", "success");
    } finally {
      setAzioneInCorso(false);
    }
  };

  const riapri = async () => {
    if (azioneInCorso) return;
    setAzioneInCorso(true);
    try {
      scriviIoStesso.current = true;
      const oggi = toLocalDateStr(new Date());
      await setDoc(doc(db, "ordiniCavi", projectId), {
        orgId, projectId, stato: "bozza", riapertoDa: currentUser?.uid || "", riapertoDaNome: currentUser?.displayName || "", riapertoData: oggi,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      logModifica({ orgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "ordiniCavi", refId: projectId, refNome: `Ordine Cavi — ${projectNome || ""}`,
        campo: "stato", campoLabel: "Ordine cavi riaperto", before: "inviato", after: "bozza",
        projectId, projectName: projectNome || "" });
      setStato("bozza");
      addToast("Riaperto — di nuovo modificabile", "success");
    } finally {
      setAzioneInCorso(false);
    }
  };

  const aggiornaCella = (id, key, val) => setRighe(r => r.map(row => row.id===id ? {...row,[key]:val} : row));
  const aggiungiRiga = () => setRighe(r => [...r, rigaVuota()]);
  const eliminaRiga = (id) => setRighe(r => r.filter(row => row.id !== id));

  // Importazione — stesse 13 colonne, stesso ordine, dello strumento standalone: formato
  // preciso da rispettare, non un'importazione che indovina le colonne.
  const importaExcel = (file) => {
    setErroreImport(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: false });
        const nomeFoglio = wb.SheetNames.includes("Dati Cavi") ? "Dati Cavi" : wb.SheetNames[0];
        const foglio = wb.Sheets[nomeFoglio];
        const aoa = XLSX.utils.sheet_to_json(foglio, { header: 1, defval: "" });
        if (aoa.length < 1) { setErroreImport("Il file sembra vuoto."); return; }
        const intestazioniTrovate = aoa[0].map(h => String(h||"").trim());
        const normalizzaTesto = s => s.toLowerCase().replace(/\s+/g," ").trim();
        const attesoNorm = INTESTAZIONI_ATTESE.map(normalizzaTesto);
        const trovatoNorm = intestazioniTrovate.slice(0, INTESTAZIONI_ATTESE.length).map(normalizzaTesto);
        const corrisponde = attesoNorm.length === trovatoNorm.length && attesoNorm.every((h,i) => h === trovatoNorm[i]);
        if (!corrisponde) {
          setErroreImport(`Le colonne del file non corrispondono al formato atteso. Attese, nell'ordine: ${INTESTAZIONI_ATTESE.join(" · ")}. Trovate: ${intestazioniTrovate.filter(Boolean).join(" · ") || "(nessuna intestazione riconosciuta)"}.`);
          return;
        }
        const nuoveRighe = aoa.slice(1)
          .filter(r => r.some(c => String(c||"").trim() !== ""))
          .map(r => ({
            id: nuovoId(), quadro: String(r[0]??""), pos: String(r[1]??""), sigla: String(r[2]??""), descrizione: String(r[3]??""),
            tensione: String(r[4]??""), interr: String(r[5]??""), lunghezza: String(r[6]??""), tipo: String(r[7]??""),
            colore: String(r[8]??""), formazione: String(r[9]??""), origine: String(r[10]??""), destinazione: String(r[11]??""), note: String(r[12]??""),
          }));
        if (nuoveRighe.length === 0) { setErroreImport("Il file ha le colonne giuste ma nessuna riga di dati sotto l'intestazione."); return; }
        setRighe(nuoveRighe);
        addToast("Importato ✅", "success");
      } catch (err) {
        console.error("Errore lettura file cavi:", err);
        setErroreImport("Il file non è leggibile come Excel valido.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Raggruppamento — stessa logica dello strumento standalone: una riga entra nel conteggio
  // solo se ha Tipo Cavo, Formazione e Lunghezza; il gruppo è Tipo|Colore|Formazione (colore
  // compreso: un cavo con colore indicato e uno identico senza colore restano due gruppi
  // diversi, come nell'originale — se serve un solo gruppo, il colore va compilato ovunque).
  const gruppi = [];
  const mappaGruppi = {};
  const incomplete = [];
  righe.forEach(r => {
    const haDati = r.sigla.trim() !== "" || r.descrizione.trim() !== "";
    const lunghezzaNum = parseFloat((r.lunghezza||"").toString().replace(",","."));
    const valida = r.tipo.trim() !== "" && r.formazione.trim() !== "" && !isNaN(lunghezzaNum) && lunghezzaNum > 0;
    if (haDati && !valida) { incomplete.push(r); return; }
    if (!valida) return;
    const chiave = `${r.tipo.trim().toLowerCase()}|${r.colore.trim().toLowerCase()}|${r.formazione.trim().toLowerCase()}`;
    if (!mappaGruppi[chiave]) {
      mappaGruppi[chiave] = { tipo: r.tipo.trim(), colore: r.colore.trim(), formazione: r.formazione.trim(), collegamenti: 0, lunghezzaTotale: 0, sigle: [] };
      gruppi.push(mappaGruppi[chiave]);
    }
    mappaGruppi[chiave].collegamenti += 1;
    mappaGruppi[chiave].lunghezzaTotale += lunghezzaNum;
    if (r.sigla.trim()) mappaGruppi[chiave].sigle.push(r.sigla.trim());
  });
  const totaleMetri = gruppi.reduce((s,g) => s + g.lunghezzaTotale, 0);

  // Verifiche automatiche — solo informative, non bloccano mai nulla (non impediscono di
  // salvare, inviare, o stampare). Girano solo sulle righe dove i dati scritti sono
  // abbastanza semplici da leggere senza indovinare: Formazione tipo "3x2,5", Tensione
  // 230/400V, Interruttore con un numero riconoscibile. Tutto il resto resta silenzioso,
  // non segnalato né come errore né come "verificato" — significherebbe inventare un dato.
  const verifiche = [];
  righe.forEach(r => {
    if (!r.tensione && !r.interr && !r.formazione) return;
    const sez = estraiSezioneDaFormazione(r.formazione);
    const tipo = tipoDaTensione(r.tensione);
    const In = estraiCorrenteDaInterruttore(r.interr);
    if (!sez || !tipo) return;
    const etichetta = r.sigla.trim() || r.descrizione.trim() || `riga senza sigla (${r.tipo || "?"})`;

    // Caduta di tensione — stessa formula dello strumento "Caduta di Tensione".
    const L = parseFloat((r.lunghezza||"").toString().replace(",","."));
    if (In > 0 && L > 0) {
      const deltaV = (tipo.b * RESISTIVITA_RAME * L * In * 0.9) / parseFloat(sez.sezione);
      const deltaVPerc = (deltaV / (tipo.label==="monofase" ? 230 : 400)) * 100;
      if (deltaVPerc > 4) {
        verifiche.push({ tipo:"caduta", sigla: etichetta, msg: `Caduta di tensione stimata ${(deltaVPerc).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}% (oltre il 4% di riferimento) — calcolata usando la corrente dell'interruttore come caso peggiore, non la reale corrente di impiego.` });
      }
    }
    // Coordinamento interruttore-cavo — stessa tabella (metodo B1, PVC, rame, 30°C) dello
    // strumento dedicato, dove disponibile per la sezione trovata.
    const Iz = PORTATA_B1_PVC_RAME[sez.sezione]?.[tipo.nConduttori];
    if (In > 0 && Iz && In > Iz) {
      verifiche.push({ tipo:"coordinamento", sigla: etichetta, msg: `Interruttore da ${In}A su cavo ${sez.sezione}mm² con portata di riferimento ${Iz}A (metodo B1) — sembra sovradimensionato rispetto al cavo.` });
    }
  });
  // Sigle duplicate — errore di battitura frequente su elenchi lunghi, sempre utile da vedere.
  const sigleConteggio = {};
  righe.forEach(r => { if (r.sigla.trim()) sigleConteggio[r.sigla.trim()] = (sigleConteggio[r.sigla.trim()]||0)+1; });
  Object.entries(sigleConteggio).filter(([,n])=>n>1).forEach(([sigla,n]) => {
    verifiche.push({ tipo:"duplicato", sigla, msg: `La sigla "${sigla}" compare ${n} volte nell'elenco.` });
  });

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    const righeTabella = gruppi.map(g => `<tr>
        <td>${escapeHtml(g.tipo)}</td><td>${escapeHtml(g.colore)||"—"}</td><td>${escapeHtml(g.formazione)}</td>
        <td style="text-align:center;">${g.collegamenti}</td>
        <td style="text-align:right;">${g.lunghezzaTotale.toLocaleString("it-IT",{maximumFractionDigits:1})}</td>
        <td style="font-size:9.5px;color:#6b7280;">${g.sigle.map(escapeHtml).join(", ")}</td>
      </tr>`).join("");
    const righeIncomplete = incomplete.length ? `
      <h2 style="color:#92400e;">⚠ Collegamenti con dati mancanti — da verificare prima dell'ordine</h2>
      <table><tr><th>Quadro</th><th>Sigla Cavo</th><th>Descrizione</th><th>Tipo Cavo</th><th>Formazione</th><th>L. (mt.)</th></tr>
      ${incomplete.map(r => `<tr><td>${escapeHtml(r.quadro)||"—"}</td><td>${escapeHtml(r.sigla)||"—"}</td><td>${escapeHtml(r.descrizione)||"—"}</td><td>${escapeHtml(r.tipo)||"—"}</td><td>${escapeHtml(r.formazione)||"—"}</td><td>${escapeHtml(r.lunghezza)||"—"}</td></tr>`).join("")}
      </table>` : "";
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Ordine Cavi — ${escapeHtml(projectNome||riferimento)}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:900px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:12mm;}
      h1{font-size:18px;color:#6d28d9;margin-bottom:2px;}
      h2{font-size:13px;color:#92400e;margin:18px 0 8px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:10px;}
      td,th{border:1px solid #e5e7eb;padding:6px 8px;text-align:left;font-size:11px;}
      th{background:#f9fafb;color:#374151;}
      .totale{background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:10px 14px;font-size:13px;font-weight:700;color:#6d28d9;margin-top:6px;}
      .nota{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px 14px;font-size:12px;color:#92400e;margin-top:12px;}
    </style></head><body>
      <h1>🔀 Ordine Cavi</h1>
      <div style="font-size:15px;font-weight:700;color:#374151;margin-bottom:4px;">Commessa: ${codiceCommessa ? `[${escapeHtml(codiceCommessa)}] ` : ""}${escapeHtml(projectNome) || "—"}</div>
      <div class="sub">${riferimento ? `Riferimento: ${escapeHtml(riferimento)} · ` : ""}Generato il ${oggi} con Nexiora</div>
      <table>
        <tr><th>Tipo Cavo</th><th>Colore</th><th>Formazione</th><th>N. Collegamenti</th><th>Lunghezza Tot. (m)</th><th>Sigle cavi incluse</th></tr>
        ${righeTabella || '<tr><td colspan="6" style="text-align:center;color:#9ca3af;">Nessun gruppo da ordinare</td></tr>'}
      </table>
      <div class="totale">Totale metratura: ${totaleMetri.toLocaleString("it-IT",{maximumFractionDigits:1})} m</div>
      ${righeIncomplete}
      ${nota ? `<div class="nota"><strong>Nota Ufficio Acquisti:</strong> ${escapeHtml(nota)}</div>` : ""}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#6d28d9;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  if (!caricato) return <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:12 }}>Caricamento...</div>;
  if (erroreCaricamento) return (
    <div style={{ padding:24, textAlign:"center", color:"#dc2626", fontSize:12.5, maxWidth:420, margin:"0 auto" }}>
      ⚠️ {erroreCaricamento}
    </div>
  );

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div>
          <h3 style={{ fontSize:15, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}>
            🔀 Ordine Cavi {projectNome && <span style={{ fontWeight:500, color:"#6b7280", fontSize:13 }}>— {codiceCommessa ? `[${codiceCommessa}] ` : ""}{projectNome}</span>}
            {inviato
              ? <span style={{ fontSize:10, fontWeight:700, color:"#6d28d9", background:"#f5f3ff", padding:"2px 8px", borderRadius:6, display:"flex", alignItems:"center", gap:4 }}><Lock size={10}/> INVIATO A UFFICIO ACQUISTI</span>
              : commessaChiusa
              ? <span style={{ fontSize:10, fontWeight:700, color:"#991b1b", background:"#fef2f2", padding:"2px 8px", borderRadius:6, display:"flex", alignItems:"center", gap:4 }}><Lock size={10}/> COMMESSA CHIUSA</span>
              : <span style={{ fontSize:10, fontWeight:700, color:"#6b7280", background:"#f3f4f6", padding:"2px 8px", borderRadius:6 }}>BOZZA</span>}
          </h3>
          {inviato && meta.inviatoDaNome && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>Inviato da {meta.inviatoDaNome} il {meta.inviatoData ? new Date(meta.inviatoData+"T00:00:00").toLocaleDateString("it-IT") : ""}</div>}
          {meta.riapertoDaNome && <div style={{ fontSize:11, color:"#9ca3af" }}>Ultima riapertura: {meta.riapertoDaNome} il {meta.riapertoData ? new Date(meta.riapertoData+"T00:00:00").toLocaleDateString("it-IT") : ""}</div>}
        </div>
        {salvataggioInCorso && <span style={{ fontSize:10, color:"#9ca3af" }}>Salvataggio...</span>}
      </div>

      {!bloccato && (
        <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"8px 12px", marginBottom:14, fontSize:11, color:"#6d28d9" }}>
          💜 Ogni riga è un collegamento cavo. Compilando Tipo Cavo, Formazione e Lunghezza, Nexiora li raggruppa da solo per Tipo/Colore/Formazione — l'elenco pronto per il fornitore è sotto. Attenzione: un cavo con colore indicato e uno identico senza colore vengono trattati come due articoli diversi. Su Tipo Cavo, Colore e Formazione, con l'uso Nexiora imparerà a suggerire i valori già scritti in ordini precedenti della tua azienda — inutile con i primi ordini, comodo da lì in poi.
        </div>
      )}

      <div style={{ display:"flex", gap:10, alignItems:"flex-end", marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ flex:"0 1 45%", minWidth:180 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Riferimento (facoltativo, usato in stampa)</label>
          <input value={riferimento} disabled={bloccato} onChange={e=>setRiferimento(e.target.value)} style={{...inp, background: bloccato?"#f9fafb":"#fff"}} placeholder="Es. Piano 1 — Quadro Q1" autoComplete="off"/>
        </div>
        {!bloccato && (
          <div style={{ flex:"1 1 45%", minWidth:220, background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"6px 10px", fontSize:10.5, color:"#6d28d9" }}>
            💜 <strong>Nexiora:</strong> l'importazione da Excel va a buon fine solo se il foglio ha le stesse identiche colonne, nello stesso ordine, della tabella qui sotto.
          </div>
        )}
        {!bloccato && (
          <>
            <button onClick={() => fileInputRef.current?.click()}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:"1px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
              <Upload size={13}/> Importa da Excel
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display:"none" }}
              onChange={e => { if (e.target.files[0]) importaExcel(e.target.files[0]); e.target.value=""; }} />
          </>
        )}
      </div>

      {erroreImport && (
        <div style={{ display:"flex", gap:8, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 12px", marginBottom:14, fontSize:11.5, color:"#991b1b" }}>
          <AlertTriangle size={15} style={{ flexShrink:0, marginTop:1 }}/>
          <div><strong>Importazione non riuscita — le colonne devono corrispondere esattamente.</strong><div style={{ marginTop:4 }}>{erroreImport}</div></div>
        </div>
      )}

      <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:6 }}>Elenco cavi</div>
      <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:8, opacity: bloccato?0.75:1 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11.5, tableLayout:"auto" }}>
          <thead>
            <tr style={{ background:"#f9fafb" }}>
              {COLONNE.map(c => <th key={c.key} style={{ padding:"6px 6px", textAlign:"left", color:"#6b7280", fontWeight:600, minWidth:c.w, borderBottom:"1px solid #e5e7eb" }}>{c.label}</th>)}
              <th style={{ width:32 }}></th>
            </tr>
          </thead>
          <tbody>
            {righe.map(r => (
              <tr key={r.id}>
                {COLONNE.map(c => (
                  <td key={c.key} style={{ padding:3 }}>
                    <input value={r[c.key]} disabled={bloccato} onChange={e=>aggiornaCella(r.id, c.key, e.target.value)} style={inp} autoComplete="off" name={`${c.key}-${r.id}`}
                      list={c.key==="tipo" ? "suggerimenti-tipo-cavo" : c.key==="colore" ? "suggerimenti-colore" : c.key==="formazione" ? "suggerimenti-formazione" : undefined} />
                  </td>
                ))}
                <td style={{ padding:3, textAlign:"center" }}>
                  {!bloccato && <button onClick={()=>eliminaRiga(r.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"#dc2626", padding:2 }}><Trash2 size={13}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!bloccato && (
        <button onClick={aggiungiRiga} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, fontWeight:700, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", marginBottom:20 }}>
          <Plus size={13}/> Aggiungi riga
        </button>
      )}
      <datalist id="suggerimenti-tipo-cavo">{suggerimentiStorici.tipi.map(v => <option key={v} value={v}/>)}</datalist>
      <datalist id="suggerimenti-colore">{suggerimentiStorici.colori.map(v => <option key={v} value={v}/>)}</datalist>
      <datalist id="suggerimenti-formazione">{suggerimentiStorici.formazioni.map(v => <option key={v} value={v}/>)}</datalist>

      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:8 }}>Ordine per il fornitore — raggruppato</div>
      {gruppi.length === 0 ? (
        <div style={{ textAlign:"center", padding:24, color:"#9ca3af", fontSize:12 }}>Compila Tipo Cavo, Formazione e Lunghezza su almeno una riga per vedere l'ordine raggruppato.</div>
      ) : (
        <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:10 }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#faf5ff" }}>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Tipo Cavo</th>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Colore</th>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Formazione</th>
                <th style={{ padding:"7px 10px", textAlign:"right", color:"#6d28d9" }}>N. Collegamenti</th>
                <th style={{ padding:"7px 10px", textAlign:"right", color:"#6d28d9" }}>Lunghezza Tot. (m)</th>
                <th style={{ padding:"7px 10px", textAlign:"left", color:"#6d28d9" }}>Sigle cavi incluse</th>
              </tr>
            </thead>
            <tbody>
              {gruppi.map((g,i) => (
                <tr key={i} style={{ borderTop:"1px solid #f3f4f6" }}>
                  <td style={{ padding:"7px 10px", fontWeight:600 }}>{g.tipo}</td>
                  <td style={{ padding:"7px 10px", color:"#6b7280" }}>{g.colore||"—"}</td>
                  <td style={{ padding:"7px 10px" }}>{g.formazione}</td>
                  <td style={{ padding:"7px 10px", textAlign:"right" }}>{g.collegamenti}</td>
                  <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:700 }}>{g.lunghezzaTotale.toLocaleString("it-IT",{maximumFractionDigits:1})}</td>
                  <td style={{ padding:"7px 10px", color:"#9ca3af", fontSize:11 }}>{g.sigle.length ? g.sigle.join(", ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {incomplete.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12.5, fontWeight:700, color:"#92400e", marginBottom:6 }}>
            <AlertTriangle size={14}/> {incomplete.length} collegament{incomplete.length>1?"i":"o"} con dati mancanti — da verificare prima dell'ordine
          </div>
          <div style={{ overflowX:"auto", border:"1px solid #fde68a", borderRadius:8 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11.5 }}>
              <thead>
                <tr style={{ background:"#fffbeb" }}>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Quadro</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Sigla Cavo</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Descrizione</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Tipo Cavo</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>Formazione</th>
                  <th style={{ padding:"6px 10px", textAlign:"left", color:"#92400e" }}>L. (mt.)</th>
                </tr>
              </thead>
              <tbody>
                {incomplete.map(r => (
                  <tr key={r.id} style={{ borderTop:"1px solid #fef3c7" }}>
                    <td style={{ padding:"6px 10px" }}>{r.quadro||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.sigla||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.descrizione||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.tipo||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.formazione||"—"}</td>
                    <td style={{ padding:"6px 10px" }}>{r.lunghezza||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {verifiche.length > 0 && (
        <div style={{ marginBottom:16, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"10px 14px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700, color:"#1d4ed8", marginBottom:8 }}>
            <Info size={14}/> Verifiche automatiche — solo informative, non bloccano nulla
          </div>
          {verifiche.map((v,i) => (
            <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:6, fontSize:11.5, color:"#1e40af", marginBottom:5 }}>
              {v.tipo==="duplicato" ? <Copy size={12} style={{ flexShrink:0, marginTop:2 }}/> : <AlertTriangle size={12} style={{ flexShrink:0, marginTop:2 }}/>}
              <span><strong>{v.sigla}</strong> — {v.msg}</span>
            </div>
          ))}
          <div style={{ fontSize:10, color:"#93c5fd", marginTop:6 }}>
            Basate su ipotesi semplificate (metodo di posa B1, PVC, rame, cos φ 0,9, corrente dell'interruttore come caso peggiore) — un controllo a colpo d'occhio, non sostituiscono una verifica progettuale.
          </div>
        </div>
      )}

      <div style={{ marginBottom:20 }}>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>
          Nota Ufficio Acquisti {!canGestireAcquisti && "(sola lettura)"}
        </label>
        <textarea value={nota} disabled={!canGestireAcquisti || commessaChiusa} onChange={e=>salvaNota(e.target.value)} rows={2}
          style={{...inp, resize:"vertical", background: canGestireAcquisti?"#fff":"#f9fafb"}} placeholder="Es. Ordinato il 3/9, consegna prevista 10gg"/>
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", borderTop:"1px solid #f3f4f6", paddingTop:14 }}>
        <button onClick={stampaPDF} disabled={gruppi.length===0}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
            color: gruppi.length>0 ? "#6d28d9" : "#d1d5db", background: gruppi.length>0 ? "#faf5ff" : "#f9fafb",
            border:`1px solid ${gruppi.length>0?"#e9d5ff":"#e5e7eb"}`, borderRadius:8, padding:"9px 18px",
            cursor: gruppi.length>0 ? "pointer" : "not-allowed" }}>
          <Printer size={13}/> Stampa PDF
        </button>

        {!inviato && !commessaChiusa && (
          <button onClick={invia} disabled={gruppi.length===0 || azioneInCorso}
            title={gruppi.length===0 ? "Serve almeno un gruppo per inviare" : "Invia a Ufficio Acquisti"}
            style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700, color:"#fff",
              background: (gruppi.length>0 && !azioneInCorso) ? "#6d28d9" : "#d1d5db", border:"none", borderRadius:8, padding:"9px 18px",
              cursor: (gruppi.length>0 && !azioneInCorso) ? "pointer" : "not-allowed" }}>
            <Send size={13}/> {azioneInCorso ? "Invio..." : "Invia a Ufficio Acquisti"}
          </button>
        )}
        {inviato && canGestireAcquisti && !commessaChiusa && (
          <button onClick={riapri} disabled={azioneInCorso}
            style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700, color:"#374151", background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:8, padding:"9px 18px", cursor: azioneInCorso?"not-allowed":"pointer" }}>
            <RotateCcw size={13}/> {azioneInCorso ? "..." : "Riapri"}
          </button>
        )}
        {commessaChiusa && (
          <span style={{ fontSize:11, color:"#991b1b", fontStyle:"italic" }}>Commessa chiusa — riapri prima la commessa per intervenire qui</span>
        )}
      </div>
    </div>
  );
}
